<template>
  <div class="max-w-7xl mx-auto pb-10 relative space-y-6">

    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <Activity class="w-6 h-6 text-emerald-500" />
          Métricas Globales
        </h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Monitor de salud financiera, consumo de tokens de Groq e infraestructura de WhatsApp.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button
          @click="fetchMetrics"
          :disabled="isLoading"
          class="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 transition-all shadow-xs"
        >
          <RefreshCw class="w-3.5 h-3.5" :class="{ 'animate-spin': isLoading }" />
          Recargar
        </button>
        <div class="flex items-center gap-2 bg-white dark:bg-slate-900 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs text-xs">
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span class="font-bold text-slate-700 dark:text-slate-300">Sistema Operativo</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center py-24">
      <Loader2 class="w-8 h-8 text-emerald-500 animate-spin" />
    </div>

    <div v-else class="space-y-6">
      <!-- 1. KPIs PRINCIPALES -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

        <!-- KPI: Facturación CRM -->
        <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div class="flex justify-between items-start mb-4">
            <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <TrendingUp class="w-5 h-5" />
            </div>
            <span class="text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-md">Ventas Ganadas</span>
          </div>
          <h3 class="text-2xl font-black text-slate-900 dark:text-white font-mono">
            $ {{ formatNumber(metrics.activity.total_revenue) }}
          </h3>
          <p class="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Ingresos Registrados</p>
        </div>

        <!-- KPI: Inquilinos -->
        <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div class="flex justify-between items-start mb-4">
            <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <Users class="w-5 h-5" />
            </div>
            <span class="text-xs font-bold text-slate-500 flex items-center gap-1">
              <Crown class="w-3.5 h-3.5 text-amber-500" /> {{ metrics.users.premium }} Premium
            </span>
          </div>
          <h3 class="text-2xl font-black text-slate-900 dark:text-white font-mono">
            {{ metrics.users.total }}
          </h3>
          <p class="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">
            Inquilinos ({{ metrics.users.active }} activos)
          </p>
        </div>

        <!-- KPI: Tokens Reales Groq -->
        <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div class="flex justify-between items-start mb-4">
            <div class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-500/10 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <Cpu class="w-5 h-5" />
            </div>
            <span class="text-xs font-bold text-purple-600 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-md">
              {{ metrics.activity.total_messages }} msgs
            </span>
          </div>
          <h3 class="text-2xl font-black text-slate-900 dark:text-white font-mono">
            {{ formatShortNumber(metrics.activity.total_groq_tokens) }}
          </h3>
          <p class="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Tokens Consumidos (Groq)</p>
        </div>

        <!-- KPI: Instancias WhatsApp -->
        <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div class="flex justify-between items-start mb-4">
            <div class="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-orange-600 dark:text-orange-400">
              <Zap class="w-5 h-5" />
            </div>
            <span class="text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-md">
              {{ metrics.agents.active }} Activos
            </span>
          </div>
          <h3 class="text-2xl font-black text-slate-900 dark:text-white font-mono">
            {{ metrics.agents.instances }}
          </h3>
          <p class="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Instancias Evolution API</p>
        </div>

      </div>

      <!-- 2. PANELES DE DETALLE -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <!-- Top 5 Consumo de Tokens Real -->
        <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
              <Server class="w-4 h-4 text-purple-500" />
              Top 5 Consumo de Tokens (Groq)
            </h3>
            <span class="text-xs text-slate-400">Auditado por mensajes</span>
          </div>

          <div v-if="metrics.top_tenants.length === 0" class="text-center py-8 text-xs text-slate-400">
            No hay consumo de tokens registrado aún.
          </div>

          <div v-else class="space-y-5">
            <div v-for="(tenant, index) in metrics.top_tenants" :key="index" class="relative">
              <div class="flex justify-between text-xs mb-1.5 font-semibold">
                <span class="text-slate-700 dark:text-slate-300">{{ tenant.name }}</span>
                <span class="font-mono text-purple-600 dark:text-purple-400">{{ Number(tenant.tokens).toLocaleString() }} tokens</span>
              </div>
              <div class="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                <div
                  class="bg-purple-500 h-2 rounded-full transition-all duration-700"
                  :style="`width: ${tenant.percentage}%`"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Salud de Infraestructura & Leads -->
        <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2 mb-6">
            <AlertCircle class="w-4 h-4 text-emerald-500" />
            Infraestructura & Conversión
          </h3>

          <div class="space-y-4 flex-1">
            <div class="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700/50">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
                  <Zap class="w-4 h-4" />
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-800 dark:text-slate-200">Evolution API (v2.3.7)</p>
                  <p class="text-[11px] text-slate-500">Instancia conectada</p>
                </div>
              </div>
              <span class="px-2.5 py-1 text-[10px] font-bold uppercase rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800">
                Operativo
              </span>
            </div>

            <div class="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700/50">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600">
                  <Cpu class="w-4 h-4" />
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-800 dark:text-slate-200">Tokens de Entrada / Salida</p>
                  <p class="text-[11px] text-slate-500">
                    Prompt: {{ formatShortNumber(metrics.activity.total_prompt_tokens) }} | Comp: {{ formatShortNumber(metrics.activity.total_completion_tokens) }}
                  </p>
                </div>
              </div>
              <span class="px-2.5 py-1 text-[10px] font-bold uppercase rounded-full bg-purple-50 text-purple-600 border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
                Groq API
              </span>
            </div>

            <div class="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700/50">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
                  <Target class="w-4 h-4" />
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-800 dark:text-slate-200">Leads Calificados (Global)</p>
                  <p class="text-[11px] text-slate-500">Prospectos capturados por agentes</p>
                </div>
              </div>
              <span class="font-mono font-bold text-slate-900 dark:text-white text-sm">
                {{ metrics.activity.total_leads }} leads
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import {
  Activity, TrendingUp, Users, Crown, Zap, Server, AlertCircle, RefreshCw, Loader2, Target, Cpu
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';

const { showError } = useToast();
const isLoading = ref(true);

const metrics = ref({
  users: { total: 0, premium: 0, active: 0 },
  agents: { total: 0, instances: 0, active: 0 },
  activity: {
    total_messages: 0,
    total_groq_tokens: 0,
    total_prompt_tokens: 0,
    total_completion_tokens: 0,
    total_leads: 0,
    total_revenue: 0
  },
  top_tenants: []
});

const fetchMetrics = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/get_global_metrics');
    if (res.data?.data) {
      metrics.value = res.data.data;
    }
  } catch (err) {
    showError('Error al sincronizar métricas globales.');
  } finally {
    isLoading.value = false;
  }
};

const formatNumber = (val) => {
  return Number(val || 0).toLocaleString('es-NI', { minimumFractionDigits: 2 });
};

const formatShortNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toLocaleString();
};

onMounted(() => {
  fetchMetrics();
});
</script>